import ParametersWidget from '../common/Parameters';
import { DefaultHeadlineType, LastDonation, WidgetData } from '../types/commonType';

export const TITLE_APP = 'Wilderness International Widget |';
export const isBrowserLocal = () => typeof localStorage !== 'undefined';
export const DEFAULT_FONT = 'Arial';

export enum LanguageCode {
  EN = 'EN',
  DE = 'DE',
}

export const WIDGET_TYPE = {
  DONATION: 'donation',
  TOTAL: 'total',
};

export const DEFAULT_HEADLINE: DefaultHeadlineType = {
  DONATION: {
    EN: 'Last Donation',
    DE: 'Letzte Spende',
  },
  TOTAL: {
    EN: 'Total',
    DE: 'Summe',
  },
};

export const LanguageList = [
  { name: LanguageCode.EN, code: LanguageCode.EN },
  { name: LanguageCode.DE, code: LanguageCode.DE },
];

export const getLanguageDefault = () => {
  if (isBrowserLocal()) {
    const getLanguage = localStorage.getItem('language_code') || 'de';
    return LanguageList.find(lan => lan.code === getLanguage.toUpperCase()) || LanguageList[0];
  }
  return LanguageList[0];
};

export enum DISPLAY_CODE {
  SQM = 'sqm',
  AMOUNT = 'amount',
  co2 = 'co2',
}

export enum ALIGN_CODE {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export const CURRENCY_CODE = {
  EUR: 'EUR',
  CAD: 'CAD',
  CHF: 'CHF',
};

export enum VISIBILITY_CODE {
  ALL = '',
  NAME = 'name',
  VALUE = 'value',
}

export enum UNIT_CODE {
  KG = 'kgs',
  TONNE = 'tonnes',
}

const getUnitByCode = (parameters: ParametersWidget) => {
  switch (parameters?.getUnitValue()) {
    case UNIT_CODE.KG:
      return ' kg';
    case UNIT_CODE.TONNE:
      return parameters?.getLanguageValue()?.toUpperCase() === LanguageCode.EN ? ' tonnes' : ' Tonnen';
    default:
      return ' kg';
  }
};

const getCO2AmountInUnit = (unit: string, co2: number) => {
  switch (unit) {
    case UNIT_CODE.KG:
      return co2;
    case UNIT_CODE.TONNE:
      return convertKgToTon(co2);
    default:
      return co2;
  }
};

export const loadGoogleFont = (fontName: string) => {
  const style = document.createElement('style');
  style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}:wght@300;400;500;600;700&display=swap');
    `;
  document.head.appendChild(style);
};

export const convertKgToTon = (kg: number) => {
  return kg >= 1000 ? Math.trunc(kg / 1000) : Math.floor((kg / 1000) * 10) / 10;
};

export const getCurrentWidgetAmount = (parameters: ParametersWidget, sqm: number, amount: number, co2: number, widgetData: WidgetData) => {
  if (widgetData) {
    switch (parameters?.getDisplayValueValue()) {
      case DISPLAY_CODE.SQM:
        return sqm || 0;
      case DISPLAY_CODE.AMOUNT:
        return amount || 0;
      case DISPLAY_CODE.co2:
        return getCO2AmountInUnit(parameters?.getUnitValue(), co2) || 0;
      default:
        return 'N/A';
    }
  } else {
    return 'N/A';
  }
};

/**
 * Format animated number with proper unit and locale
 * @param animatedValue - The animated number value
 * @param parameters - Widget parameters containing displayValue, language, unit
 * @returns Formatted string with number and unit
 */
export const formatAnimatedNumber = (animatedValue: number, parameters: ParametersWidget, targetNumber: number | 'N/A' = 0): string => {
  let formattedNumber = animatedValue.toLocaleString(parameters?.getLanguageValue()?.toLowerCase() || 'en');

  if (formattedNumber !== 'N/A') {
    if (targetNumber !== animatedValue) {
      formattedNumber = (+animatedValue?.toFixed(0)).toLocaleString(parameters?.getLanguageValue()?.toLowerCase() || 'en');
    }
    switch (parameters?.getDisplayValueValue()) {
      case DISPLAY_CODE.SQM:
        return `${formattedNumber} m²`;
      case DISPLAY_CODE.AMOUNT:
        return `${formattedNumber} ${CURRENCY_CODE.EUR}`;
      case DISPLAY_CODE.co2:
        return `${formattedNumber}${getUnitByCode(parameters)}`;
      default:
        return formattedNumber;
    }
  } else {
    return 'N/A';
  }
};

/**
 * Format animated donation number with proper unit and locale
 * @param animatedValue - The animated number value
 * @param parameters - Widget parameters containing displayValue, language, unit
 * @param last_donation - Donation data for currency info
 * @returns Formatted string with number and unit
 */
export const formatAnimatedDonationNumber = (
  animatedValue: number,
  parameters: ParametersWidget,
  last_donation: LastDonation,
  targetNumber: number | 'N/A' = 0,
): string => {
  let formattedNumber = animatedValue.toLocaleString(parameters?.getLanguageValue()?.toLowerCase() || 'en');

  if (formattedNumber !== 'N/A') {
    if (targetNumber !== animatedValue) {
      formattedNumber = (+animatedValue?.toFixed(0)).toLocaleString(parameters?.getLanguageValue()?.toLowerCase() || 'en');
    }
    switch (parameters?.getDisplayValueValue()) {
      case DISPLAY_CODE.SQM:
        return `${formattedNumber} m²`;
      case DISPLAY_CODE.AMOUNT:
        return `${formattedNumber} ${last_donation?.currency?.toUpperCase() || CURRENCY_CODE.EUR}`;
      case DISPLAY_CODE.co2:
        return `${formattedNumber}${getUnitByCode(parameters)}`;
      default:
        return formattedNumber;
    }
  } else {
    return 'N/A';
  }
};
